import { useIntl } from 'react-intl';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors';
import { getUserRoles } from '@/store/global/userRoles/selectors';
import { CheckboxGroup, type CheckboxItem } from '../CheckboxGroup';

type FilterEntry = { id: string; value: string[] };

type Props = {
  onFilter?: (filters: FilterEntry[]) => void;
  value?: string[];
};

export default function Measurement({ onFilter, value = [] }: Props) {
  const { formatMessage } = useIntl();
  const user = useGlobalStore(getUser);
  const userRoles = useGlobalStore(getUserRoles);

  const options: CheckboxItem[] = [
    {
      id: 'poiAttribution',
      label: formatMessage({
        defaultMessage: 'POI based Attribution',
        id: 'campaigns.lineItems.advancedFilters.measurement.poiAttribution'
      })
    },
    {
      id: 'proximityAttribution',
      label: formatMessage({
        defaultMessage: 'Proximity based Attribution',
        id: 'campaigns.lineItems.advancedFilters.measurement.proximityAttribution'
      })
    },
    {
      id: 'postBid',
      label: formatMessage({
        defaultMessage: 'PostBid',
        id: 'campaigns.lineItems.advancedFilters.measurement.postBid'
      }),
      tooltipText: formatMessage({
        defaultMessage:
          'Measurement and verification that occurs after the ad has been served to confirm the actual delivery, viewability, and environment quality of the impression.',
        id: 'campaigns.lineItems.advancedFilters.measurement.postBid.tooltip'
      })
    },
    {
      id: 'conversionPixels',
      label: formatMessage({
        defaultMessage: 'Conversion Pixels',
        id: 'campaigns.lineItems.advancedFilters.measurement.conversionPixels'
      }),
      tooltipText: formatMessage({
        defaultMessage:
          'Small snippets of code placed on specific website pages to track and attribute successful user actions back to your campaign.',
        id: 'campaigns.lineItems.advancedFilters.measurement.conversionPixels.tooltip'
      })
    },
    {
      dividerText: formatMessage({
        defaultMessage: 'PreBid',
        id: 'campaigns.lineItems.advancedFilters.measurement.preBid.divider'
      }),
      id: 'preBidDivider'
    },
    {
      id: 'adFraud',
      label: formatMessage({
        defaultMessage: 'Ad Fraud',
        id: 'campaigns.lineItems.advancedFilters.preBid.adFraud'
      }),
      show: checkUserResourcePermissions('adFraud', user.toJSON(), userRoles),
      tooltipText: formatMessage({
        defaultMessage:
          'A proactive security layer that analyzes traffic quality before a bid is placed, blocking bots, non-human traffic, and suspicious sources to protect your budget.',
        id: 'campaigns.lineItems.advancedFilters.preBid.adFraud.tooltip'
      })
    },
    {
      id: 'viewability',
      label: formatMessage({
        defaultMessage: 'Viewability',
        id: 'campaigns.lineItems.advancedFilters.preBid.viewability'
      }),
      tooltipText: formatMessage({
        defaultMessage:
          'A predictive tool that evaluates the likelihood of an ad being seen by a real human before you bid, prioritizing placements with high visibility scores.',
        id: 'campaigns.lineItems.advancedFilters.preBid.viewability.tooltip'
      })
    },
    {
      id: 'brandSafety',
      label: formatMessage({
        defaultMessage: 'Brand Safety',
        id: 'campaigns.lineItems.advancedFilters.preBid.brandSafety'
      }),
      show: checkUserResourcePermissions('brandSafety', user.toJSON(), userRoles),
      tooltipText: formatMessage({
        defaultMessage:
          'A pre-auction filter that ensures your ads never appear next to content that is harmful, inappropriate, or inconsistent with your brand values.',
        id: 'campaigns.lineItems.advancedFilters.preBid.brandSafety.tooltip'
      })
    }
  ];

  const handleChange = (_id: string, selected: string[]) => {
    const filter: FilterEntry[] = [{ id: 'measurement', value: selected }];
    onFilter?.(filter);
  };

  return (
    <CheckboxGroup
      id="measurement"
      onChange={handleChange}
      options={options}
      selected={value}
      title={formatMessage({
        defaultMessage: 'Measurement',
        id: 'campaigns.lineItems.advancedFilters.measurement.title'
      })}
    />
  );
}
